import React from 'react';
import { InfoIcon } from 'lucide-react';
import { ArchitecturalForm } from '../components/ui/ArchitecturalForm';
import { ButtonLink } from '../components/ui/ButtonLink';
import { CtaBand } from '../components/ui/CtaBand';
import { PageHero } from '../components/ui/PageHero';
import { industries } from '../data/industries';
import { usePageMeta } from '../hooks/usePageMeta';
export function Industries() {
  usePageMeta('Industries', 'Executive search across financial services, technology, healthcare, energy, manufacturing, professional services, consumer, logistics, legal and governance, and public policy.');
  return <>
      <PageHero label="Industries" title="Leadership expertise across ten sectors" intro="Every sector brings distinct leadership challenges, regulatory contexts, and talent markets. These are the areas Aurevane Search Partners is positioned to serve." visual={<ArchitecturalForm variant="frame" tone="dark" data-id="element-840" data-mp="1jmzeki9l1e25" />} data-id="element-839" data-mp="1xjw3ge654lge" />

      <section className="border-b border-plum/10 bg-porcelain" data-id="element-841" data-mp="1qnahynw5r7ts">
        <div className="mx-auto max-w-7xl px-6 py-8 lg:px-10" data-id="element-842" data-mp="1ve94i57qp25h">
          <p className="flex max-w-3xl gap-3 text-sm leading-relaxed text-plum/70" data-id="element-843" data-mp="1vfu0lz9pct5g">
            <InfoIcon className="mt-0.5 h-4 w-4 shrink-0 text-copper-deep" aria-hidden="true" data-id="element-844" data-mp="1ho5kzm08vmjq" />
            The sectors below describe where we are positioned to support leadership appointments. They are not a record of completed assignments.
          </p>
          <nav aria-label="Industries on this page" className="mt-6" data-id="element-845" data-mp="2ecv5lk39at9e">
            <ul className="flex flex-wrap gap-2" data-id="element-846" data-mp="2eb0t8u61w9gx">
              {industries.map(i => <li key={i.slug} data-id="element-847" data-mp="20d3xu13c293u">
                  <a href={`#${i.slug}`} className="inline-flex h-9 items-center rounded-full border border-plum/15 px-4 text-sm text-plum/75 transition-colors duration-150 hover:border-copper hover:text-copper-deep" data-id="element-848" data-mp="25c6pmjuayitv">
                    {i.name}
                  </a>
                </li>)}
            </ul>
          </nav>
        </div>
      </section>

      {industries.map((industry, index) => {
      const Icon = industry.icon;
      const alt = index % 2 === 1;
      const lists = [{
        title: 'Typical leadership challenges',
        items: industry.challenges
      }, {
        title: 'Relevant executive functions',
        items: industry.functions
      }, {
        title: 'Example roles',
        items: industry.roles
      }];
      return <section key={industry.slug} id={industry.slug} aria-labelledby={`${industry.slug}-title`} className={alt ? 'bg-limestone' : 'bg-porcelain'} data-id="element-849" data-mp="3iapcm6t77p6m" data-mpi={index}>
            <div className="mx-auto grid max-w-7xl gap-12 px-6 py-20 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:py-24" data-id="element-850" data-mp="3ebhpqfwhlwx4">
              <div className="lg:col-span-5" data-id="element-851" data-mp="1ci0fr5kvwjz7">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-aubergine text-copper-light" data-id="element-852" data-mp="1cgpg9hyh1ydw">
                  <Icon className="h-5 w-5" aria-hidden="true" data-id="element-853" data-mp="17jp5wuula9gz" />
                </span>
                <h2 id={`${industry.slug}-title`} className="mt-6 text-3xl font-semibold leading-tight tracking-tight text-aubergine" data-id="element-854" data-mp="1f1x4mkpzrnou">
                  {industry.name}
                </h2>
                <p className="mt-5 leading-relaxed text-plum/75" data-id="element-855" data-mp="1otkpg2s1m6hc">{industry.intro}</p>
                <div className="mt-8 border-l-2 border-copper pl-5" data-id="element-856" data-mp="1tqb5wr7rf43a">
                  <h3 className="font-display text-sm font-semibold text-aubergine" data-id="element-857" data-mp="w3ua5qeocyjn">How executive search can help</h3>
                  <p className="mt-2 text-sm leading-relaxed text-plum/75" data-id="element-858" data-mp="111vtmcg0izvn">{industry.support}</p>
                </div>
                <ButtonLink to={`/employers?industry=${industry.slug}#inquiry`} variant="dark" arrow className="mt-8" ariaLabel={`Discuss a leadership search in ${industry.name}`} data-id="element-859" data-mp="160yzu07qbc57">
                  Discuss a Search in This Sector
                </ButtonLink>
              </div>
              <div className="grid gap-10 self-start sm:grid-cols-3 lg:col-span-7" data-id="element-860" data-mp="2k0k1jdwdlyhx">
                {lists.map(list => <div key={list.title} className="border-t border-plum/15 pt-5" data-id="element-861" data-mp="2f3tlgqkkn2wi">
                    <h3 className="font-display text-sm font-semibold text-aubergine" data-id="element-862" data-mp="2u0kp5c1a30rx">{list.title}</h3>
                    <ul className="mt-4 space-y-3" data-id="element-863" data-mp="2ozn66x8g22pd">
                      {list.items.map(item => <li key={item} className="text-sm leading-relaxed text-plum/75" data-id="element-864" data-mp="32ywihltj4co2">
                          {item}
                        </li>)}
                    </ul>
                  </div>)}
              </div>
            </div>
          </section>;
    })}

      <CtaBand title="Leadership needs that span sectors?" text="Many leaders bring experience that transfers across industries. Tell us about your organization and the leadership it needs." data-id="element-865" data-mp="37rppkgcgzx3b">
        <ButtonLink to="/employers#inquiry" size="lg" data-id="element-866" data-mp="3hn0xoftdi623">
          Discuss Your Hiring Needs
        </ButtonLink>
        <ButtonLink to="/candidates#introduce" variant="outline-light" size="lg" data-id="element-867" data-mp="974ir5fqbpzj">
          Introduce Yourself
        </ButtonLink>
      </CtaBand>
    </>;
}